import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useRef, useState } from 'react'
import { apiRequest } from '@/lib/supabase'
import { uploadProfileImage } from '@/lib/uploadImage'

export interface SettingsData {
  slug: string
  title: string
  bio: string | null
  profile_image_url: string | null
  display_name: string | null
  handle: string | null
  sticky_header: boolean
  seo_title: string | null
  seo_description: string | null
  is_published: boolean
  custom_domain: string | null
  default_utm_source: string | null
  default_utm_medium: string | null
  default_utm_campaign: string | null
  default_referral_id: string | null
  password_hash: string | null
  meta_pixel_id: string | null
  ga_measurement_id: string | null
  tiktok_pixel_id: string | null
}

interface Props {
  pageId: string
  data: SettingsData
  onChange: (updates: Partial<SettingsData>) => void
}

export function BioSettingsEditor({ pageId, data, onChange }: Props) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [advancedExpanded, setAdvancedExpanded] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest(`/api/bio-pages/${pageId}`, 'DELETE'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bio-pages'] })
      navigate('/bio-pages')
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: () => apiRequest('/api/bio-pages', 'POST', {
      ...data,
      slug: `${data.slug}-copy`,
      title: `${data.title} (Copy)`,
      is_published: false,
    }) as Promise<{ data: { id: string } }>,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bio-pages'] })
      navigate(`/bio-pages/${response.data.id}`)
    },
  })

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadError(null)
    setUploading(true)
    try {
      const url = await uploadProfileImage(file)
      onChange({ profile_image_url: url })
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8 pb-8">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-white/10 pb-4">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Page Settings</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configure your bio page details and visibility</p>
      </div>

      {/* Basic Information Section */}
      <section>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Page Title</label>
            <input
              type="text"
              value={data.title}
              onChange={e => onChange({ title: e.target.value })}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
              placeholder="My Bio Page"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Page URL</label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">@</span>
              <input
                type="text"
                value={data.slug}
                onChange={e => onChange({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 30) })}
                className="flex-1 px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                placeholder="username"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">urlzippy.com/@{data.slug || 'username'}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio</label>
            <textarea
              value={data.bio || ''}
              onChange={e => onChange({ bio: e.target.value })}
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
              placeholder="Tell your audience about yourself..."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 text-right">{(data.bio || '').length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Profile Picture</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="relative w-16 h-16 rounded-full overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-[#2B6CEE] dark:hover:border-[#2B6CEE] transition-colors group flex-shrink-0"
              >
                {uploading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-white/10">
                    <span className="material-symbols-outlined text-gray-400 animate-spin text-[20px]">progress_activity</span>
                  </div>
                ) : data.profile_image_url ? (
                  <>
                    <img
                      src={data.profile_image_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="material-symbols-outlined text-white text-[20px]">edit</span>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-white/5 group-hover:bg-gray-100 dark:group-hover:bg-white/10 transition-colors">
                    <span className="material-symbols-outlined text-gray-400 text-[24px]">add_a_photo</span>
                  </div>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#2B6CEE] rounded-lg hover:bg-[#2454c7] disabled:opacity-50 transition-colors"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {data.profile_image_url && (
                    <button
                      type="button"
                      onClick={() => onChange({ profile_image_url: null })}
                      className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">JPG, PNG, GIF, or WebP. Max 2MB.</p>
                {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}
              </div>
            </div>
            <input
              type="text"
              value={data.profile_image_url || ''}
              onChange={e => onChange({ profile_image_url: e.target.value || null })}
              className="mt-2 w-full px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
              placeholder="Or paste image URL..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Display Name</label>
              <input
                type="text"
                value={data.display_name || ''}
                onChange={e => onChange({ display_name: e.target.value || null })}
                maxLength={30}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                placeholder="Your Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username</label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">@</span>
                <input
                  type="text"
                  value={data.handle || ''}
                  onChange={e => onChange({ handle: e.target.value || null })}
                  maxLength={30}
                  className="flex-1 px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  placeholder="username"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visibility Section */}
      <section className="pt-6 border-t border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Visibility</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-white/5 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Published</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Make this page visible to visitors</p>
            </div>
            <button
              onClick={() => onChange({ is_published: !data.is_published })}
              className={`relative w-12 h-6 rounded-full transition-colors ${data.is_published ? 'bg-[#2B6CEE]' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${data.is_published ? 'translate-x-6' : ''}`} />
            </button>
          </div>
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-white/5 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Sticky Header</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Keep profile section visible while scrolling</p>
            </div>
            <button
              onClick={() => onChange({ sticky_header: !data.sticky_header })}
              className={`relative w-12 h-6 rounded-full transition-colors ${data.sticky_header ? 'bg-[#2B6CEE]' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${data.sticky_header ? 'translate-x-6' : ''}`} />
            </button>
          </div>
        </div>
      </section>

      {/* SEO Section */}
      <section className="pt-6 border-t border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Search Engine Optimization</h3>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">SEO Title</label>
            <input
              type="text"
              value={data.seo_title || ''}
              onChange={e => onChange({ seo_title: e.target.value || null })}
              maxLength={60}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
              placeholder="Custom page title for search engines"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 text-right">{(data.seo_title || '').length}/60</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">SEO Description</label>
            <textarea
              value={data.seo_description || ''}
              onChange={e => onChange({ seo_description: e.target.value || null })}
              maxLength={160}
              rows={2}
              className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
              placeholder="Custom description for search engines"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 text-right">{(data.seo_description || '').length}/160</p>
          </div>
        </div>
      </section>

      {/* Advanced Settings Section (Collapsible) */}
      <section className="pt-6 border-t border-gray-200 dark:border-white/10">
        <button
          onClick={() => setAdvancedExpanded(!advancedExpanded)}
          className="flex items-center justify-between w-full mb-4 group"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Advanced Settings</h3>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${advancedExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {advancedExpanded && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Domain <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-[#2B6CEE] bg-blue-50 dark:bg-blue-900/20 rounded">Pro</span>
              </label>
              <input
                type="text"
                value={data.custom_domain || ''}
                onChange={e => onChange({ custom_domain: e.target.value || null })}
                className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                placeholder="bio.yourdomain.com"
              />
            </div>

            <div className="flex items-center justify-between py-3 px-4 bg-gray-50 dark:bg-white/5 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Password Protection</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Require a password to view this page</p>
              </div>
              <button
                onClick={() => {
                  if (data.password_hash) {
                    onChange({ password_hash: null })
                  } else {
                    const password = prompt('Enter a password for this page:')
                    if (password) {
                      onChange({ password_hash: password })
                    }
                  }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors ${data.password_hash ? 'bg-[#2B6CEE]' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${data.password_hash ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tracking Pixels</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Meta Pixel ID</label>
                  <input
                    type="text"
                    value={data.meta_pixel_id || ''}
                    onChange={e => onChange({ meta_pixel_id: e.target.value || null })}
                    placeholder="123456789012345"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Google Analytics ID</label>
                  <input
                    type="text"
                    value={data.ga_measurement_id || ''}
                    onChange={e => onChange({ ga_measurement_id: e.target.value || null })}
                    placeholder="G-XXXXXXXXXX"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">TikTok Pixel ID</label>
                  <input
                    type="text"
                    value={data.tiktok_pixel_id || ''}
                    onChange={e => onChange({ tiktok_pixel_id: e.target.value || null })}
                    placeholder="XXXXXXXXXXXXXX"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Link Attribution</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Add UTM parameters to all links for analytics tracking</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">UTM Source</label>
                  <input
                    type="text"
                    value={data.default_utm_source || ''}
                    onChange={e => onChange({ default_utm_source: e.target.value || null })}
                    placeholder="bio"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">UTM Medium</label>
                  <input
                    type="text"
                    value={data.default_utm_medium || ''}
                    onChange={e => onChange({ default_utm_medium: e.target.value || null })}
                    placeholder="social"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">UTM Campaign</label>
                  <input
                    type="text"
                    value={data.default_utm_campaign || ''}
                    onChange={e => onChange({ default_utm_campaign: e.target.value || null })}
                    placeholder="bio_links"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Referral ID</label>
                  <input
                    type="text"
                    value={data.default_referral_id || ''}
                    onChange={e => onChange({ default_referral_id: e.target.value || null })}
                    placeholder="mybio"
                    maxLength={100}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Actions Section */}
      <section className="pt-6 border-t border-gray-200 dark:border-white/10">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => {
              if (confirm('Create a copy of this bio page?')) {
                duplicateMutation.mutate()
              }
            }}
            disabled={duplicateMutation.isPending}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-white/5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 disabled:opacity-50 transition-all"
          >
            {duplicateMutation.isPending ? 'Duplicating...' : 'Duplicate Page'}
          </button>
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete this bio page? This action cannot be undone.')) {
                deleteMutation.mutate()
              }
            }}
            disabled={deleteMutation.isPending}
            className="px-5 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-white/5 border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-all"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Bio Page'}
          </button>
        </div>
      </section>
    </div>
  )
}
